import { useState } from "react";
import { Megaphone, Mail, Phone, User, Send, Loader2, CheckCircle, Facebook, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

export default function PublicidadPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    ad_type: "web",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.ad_type) {
      toast.error("Por favor completa todos los campos");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/advertising-request`, formData);
      setSubmitted(true);
      toast.success("¡Solicitud enviada correctamente!");
    } catch (error) {
      toast.error("Error al enviar la solicitud. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", ad_type: "web" });
    setSubmitted(false);
  };

  return (
    <Layout>
      <PageHero
        section="publicidad"
        title="Publicidad"
        subtitle="Promociona tu negocio o evento con nosotros. Llegamos a miles de personas en Choluteca y Honduras."
        badge="Anúnciate"
        badgeIcon={Megaphone}
      />

      <section className="py-12 md:py-16" data-testid="advertising-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-brand-text mb-4">
                ¡Solicitud Enviada!
              </h2>
              <p className="text-brand-textSecondary mb-8">
                Hemos recibido tu solicitud de publicidad. Nos pondremos en contacto contigo pronto para darte más información sobre nuestros paquetes y precios.
              </p>
              <Button onClick={resetForm} variant="outline" className="rounded-full">
                Enviar otra solicitud
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Megaphone className="h-8 w-8 text-amber-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-brand-text mb-2">
                  Solicita Información de Publicidad
                </h2>
                <p className="text-brand-textSecondary">
                  Completa el formulario y te enviaremos nuestras opciones de publicidad
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-2">
                    <User className="inline h-4 w-4 mr-2 text-gray-400" />
                    Nombre / Empresa
                  </label>
                  <Input
                    type="text"
                    placeholder="Tu nombre o el de tu empresa"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-xl"
                    data-testid="advertising-name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      <Mail className="inline h-4 w-4 mr-2 text-gray-400" />
                      Email
                    </label>
                    <Input
                      type="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="rounded-xl"
                      data-testid="advertising-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      <Phone className="inline h-4 w-4 mr-2 text-gray-400" />
                      Teléfono / WhatsApp
                    </label>
                    <Input
                      type="tel"
                      placeholder="+504 9999-9999"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="rounded-xl"
                      data-testid="advertising-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-text mb-4">
                    Tipo de Publicidad
                  </label>
                  <RadioGroup
                    value={formData.ad_type}
                    onValueChange={(value) => setFormData({ ...formData, ad_type: value })}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    <Label
                      htmlFor="web"
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.ad_type === "web"
                          ? "border-amber-500 bg-amber-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <RadioGroupItem value="web" id="web" />
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.ad_type === "web" ? "bg-amber-500" : "bg-gray-100"
                        }`}>
                          <Globe className={`h-5 w-5 ${formData.ad_type === "web" ? "text-white" : "text-gray-500"}`} />
                        </div>
                        <div>
                          <p className="font-medium">Página Web</p>
                          <p className="text-xs text-gray-500">Banner en choluteca.net</p>
                        </div>
                      </div>
                    </Label>

                    <Label
                      htmlFor="facebook"
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.ad_type === "facebook"
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <RadioGroupItem value="facebook" id="facebook" />
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.ad_type === "facebook" ? "bg-blue-500" : "bg-gray-100"
                        }`}>
                          <Facebook className={`h-5 w-5 ${formData.ad_type === "facebook" ? "text-white" : "text-gray-500"}`} />
                        </div>
                        <div>
                          <p className="font-medium">Facebook</p>
                          <p className="text-xs text-gray-500">Publicación patrocinada</p>
                        </div>
                      </div>
                    </Label>
                  </RadioGroup>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full py-6 bg-amber-500 hover:bg-amber-600"
                    data-testid="advertising-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Solicitar Información
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                Nos pondremos en contacto contigo por email o WhatsApp.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
